import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { validatePDF } from '../utils/fileHandlers';
import { logger } from '../utils/logger';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
// const MAX_FILES = 10; // Removed hardcoded limit

interface FileUploadProps {
  onFileUpload: (files: File[]) => void;
  isProcessing?: boolean;
  // actualRemainingSlots: number; // Removed prop for actual limit
}

export function FileUpload({ 
  onFileUpload, 
  isProcessing = false 
  // actualRemainingSlots // Removed prop usage
}: FileUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // const remainingSlots = actualRemainingSlots; // Removed this calculation
  const [isDragActive, setIsDragActive] = useState(false);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      // Clear previous messages
      setError(null);
      setSuccessMessage(null);
      
      if (acceptedFiles.length === 0) {
        // Don't show an error for no files dropped
        return;
      }
      
      // Removed the check here, as useDropzone should handle maxFiles
      // if (acceptedFiles.length > remainingSlots) {
      //   throw new Error(`Cannot upload more than ${remainingSlots} resumes.`);
      // }
      
      // Check file sizes
      const oversizedFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        throw new Error(`Some files exceed the 10MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      }
      
      // Log files being validated
      logger.info(`Validating ${acceptedFiles.length} PDF files`);
      
      // Validate each file
      try {
        await Promise.all(acceptedFiles.map(async file => {
          try {
            await validatePDF(file);
          } catch (err) {
            logger.error(err instanceof Error ? err : `Validation failed for ${file.name}: ${String(err)}`);
            throw new Error(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
          }
        }));
      } catch (validationErr) {
        throw new Error(`File validation failed: ${validationErr instanceof Error ? validationErr.message : String(validationErr)}`);
      }
      
      // All validations passed
      logger.info(`All ${acceptedFiles.length} files validated successfully`);
      setSuccessMessage(`${acceptedFiles.length} file${acceptedFiles.length === 1 ? '' : 's'} accepted and ready for processing`);
      
      // Send the files to the parent component
      onFileUpload(acceptedFiles);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to process files';
      setError(message);
      // Use a simpler logging approach
      const errorMessage = err instanceof Error ? err.message : String(err);
      logger.error(`File upload error: ${errorMessage}`);
    }
  }, [onFileUpload]); // Use remainingSlots (derived from prop)

  // Custom drop zone handlers to ensure we update UI states correctly
  const handleDragEnter = useCallback(() => {
    if (!isProcessing) { // Removed remainingSlots check
      setIsDragActive(true);
    }
  }, [isProcessing]);

  const handleDragLeave = useCallback(() => {
    setIsDragActive(false);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
    accept: {
      'application/pdf': ['.pdf'],
    },
    // maxFiles: remainingSlots, // Removed maxFiles limit
    maxSize: MAX_FILE_SIZE,
    disabled: isProcessing, // Removed remainingSlots check
  });

  // Show rejection errors
  useEffect(() => {
    if (fileRejections.length > 0) {
      const errors = fileRejections.map(({ file, errors }) => 
        `${file.name}: ${errors.map(e => e.message).join(', ')}`
      );
      setError(`Invalid files:\n${errors.join('\n')}`);
      setSuccessMessage(null);
    }
  }, [fileRejections]);

  return (
    <div>
      <div
        {...getRootProps()}
        className={`p-10 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
          ${error ? 'border-red-300 bg-red-50' : ''}
          ${successMessage ? 'border-green-300 bg-green-50' : ''}
          ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {isProcessing ? (
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
        ) : successMessage ? (
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
        ) : error ? (
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
        ) : (
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} />
        )}
        <p className="text-lg text-gray-600">
          {isProcessing
            ? 'Processing files...'
            : 'Drag & drop resumes here, or click to select files'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Supports PDF files (10MB each)
        </p>
      </div>
      
      {error && (
        <div className="mt-4 p-4 bg-red-50 rounded-md flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Upload failed</h3>
            <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}
      
      {successMessage && !error && (
        <div className="mt-4 p-4 bg-green-50 rounded-md flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-green-800">Files ready</h3>
            <p className="text-sm text-green-700 mt-1">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}